import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcCorporationListItemDto, FcInsurancePayerListItemDto, FcModifyInsurancePayerListItemDto, ProcedureCodeMappingDto, ServiceSiteMappingDto, SystemGuid } from 'webcommon/shared';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService, FormGroupArray } from '../../generic/fc-list-validation/fc-list-validation.service';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-insurance-payer-list-modal',
  templateUrl: './fc-insurance-payer-list-modal.component.html',
})
export class FcInsurancePayerListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-insurance-payer-item';

  private _listItem: FcInsurancePayerListItemDto = {
    Id: '',
    ExternalID: '',
    Name: '',
    Notes: '',
    Corporation: {
      Id: '',
      ExternalID: '',
      Name: '',
      PrimaryContact: '',
      SecondaryContact: '',
      Address: {
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        Zip: '',
        CountyName: '',
        CountyCode: '',
      },
      Phone1: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone2: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone3: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Phone4: {
        PhoneNumber: '',
        PhoneType: '',
        PhoneTypeUid: '',
      },
      Email1: '',
      Email2: '',
      CorpNotes: '',
      Inactive: false,
    },
    AcceptAssignment: true,
    AcceptAssignmentLabOnly: false,
    AccountTypeUid: '',
    AlternateInsurancePlanUid: '',
    AmountAllowedFeeScheduleUid: SystemGuid.AmountAllowedListDefaultOptionUid,
    AutoWriteoffCreditTypeUid: '',
    ClaimStatusPayerUid: '',
    ClearinghouseUid: '',
    CodeValidationCarrierMapUid: '',
    CorporationUid: '',
    DemographicsClearingHouseUid: '',
    ElectronicClaimFormatUid: '',
    ElectronicPayerUid: '',
    Eligibility_ListClaimFormatUid: '',
    EligibilityClearingHouseUid: '',
    EligibilityPayerUid: '',
    EligibleCCM_CPO: false,
    EM2021Date: '',
    EnglishFormattingTemplateUid: '',
    EnrollmentClearingHouseUid: '',
    ERAPayerID: '',
    FeeScheduleUid: '',
    GroupIDFormat: '',
    ICD10Date: '',
    IsEPrescribingIncentive: false,
    MapToPayerTypeCode: false,
    MemberIDFormat: '',
    NoNetwork: true,
    PreferredLaboratoriesUid: [],
    PaperClaimDefaultOptionUid: SystemGuid.PaperClaimDefaultOptionUid,
    PaperClaimFormatUid: '',
    PQRIGroupUid: '',
    PrmRuleGroupUid: '',
    ReferralRequired: false,
    ReimbursementRuleUid: '',
    RequiresABNChecking: false,
    ScrubProfileUid: '',
    SpanishFormattingTemplateUid: '',
    UseProviderFeeSchedule: true,
    ProcedureCodeMapping: [],
    ServiceSiteMapping: [],
    RelInsuranceCarrierProviderExclusion: [],
  };
  public override get listItem(): FcInsurancePayerListItemDto {
    return this._listItem;
  }
  public override set listItem(value: FcInsurancePayerListItemDto) {
    this._listItem = value;
    this.listItemCorporation = {
      ...value?.Corporation,
      ExternalID: this.listItem.ExternalID,
    };
  }
  public listItemCorporation: FcCorporationListItemDto = {
    Id: '',
    ExternalID: '',
    Name: '',
    PrimaryContact: '',
    SecondaryContact: '',
    Address: {
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      CountyName: '',
      CountyCode: '',
    },
    Phone1: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone2: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone3: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone4: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Email1: '',
    Email2: '',
    CorpNotes: '',
    Inactive: false,
  };

  public notesControl: FormControl = new FormControl<string>('');
  public inactiveControl: FormControl = new FormControl<boolean>(false);

  public contactInfoGroup = new FormGroup({
    notes: this.notesControl,
    inactive: this.inactiveControl,
  });
  public settingsGroup = new FormGroup({});
  public claimsGroup = new FormGroup({});
  public labsProvidersGroup = new FormGroup({});
  public procedureMappingGroup = new FormGroup({});
  public payerIdOverrideGroup = new FormGroup({});

  public form = new FormGroup({
    contactInfo: this.contactInfoGroup,
    settings: this.settingsGroup,
    claims: this.claimsGroup,
    labsProviders: this.labsProvidersGroup,
    procedureMapping: this.procedureMappingGroup,
    payerIdOverride: this.payerIdOverrideGroup,
  });

  constructor(
    injector: Injector,
  ) {
    super(injector);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    // Load List Item and set default validation rules
    this.loadListItem({
      Notes: {
        MaxLength: 65535,
      },
    });
  }

  public getFormValidationErrors(formGroup: FormGroupArray): number {
    return FcListValidationService.getFormValidationErrors(formGroup, this.isSubmitted);
  }

  public setForm(): void {
    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem?.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem?.Inactive);

    this.validateForm();
  }

  public override save(): void {
    this.isSubmitted = true;
    const values: any = this.form.getRawValue();
    const contactInfo = values.contactInfo;
    const settings = values.settings;
    const claims = values.claims;
    const labsProviders = values.labsProviders;
    const ProcedureCodeMapping: ProcedureCodeMappingDto[] = [];
    const ServiceSiteMapping: ServiceSiteMappingDto[] = [];

    const AcceptAssignment = claims?.AcceptAssignmentStatus === 'Accept Assignment';
    const AcceptAssignmentLabOnly = claims?.AcceptAssignmentStatus === 'Accept Assignment, Lab Only';

    values?.procedureMapping?.ProcedureCodeMapping?.forEach((item: {
      procedureCodePk: string[];
      substitutionText: string;
      mapItemUid: string;
    }) => {
      if (Array.isArray(item?.procedureCodePk) && typeof item.procedureCodePk[0] === 'string') {
        const procedureCodeMappingItem: ProcedureCodeMappingDto = {
          DisplayOrder: ProcedureCodeMapping.length,
          FindTableToItemUid: item.procedureCodePk[0],
          SubstitutionText: item?.substitutionText ?? '',
        };
        if (item.mapItemUid) {
          procedureCodeMappingItem.MapItemUid = item.mapItemUid;
        }
        ProcedureCodeMapping.push(procedureCodeMappingItem);
      }
    });
    values?.payerIdOverride?.ServiceSiteMapping?.forEach((item: {
      serviceSitePk: string[];
      substitutionText: string;
      mapItemUid: string;
    }) => {
      if (Array.isArray(item?.serviceSitePk) && typeof item.serviceSitePk[0] === 'string') {
        const serviceSiteMappingItem: ServiceSiteMappingDto = {
          DisplayOrder: ServiceSiteMapping.length,
          FindTableToItemUid: item.serviceSitePk[0],
          SubstitutionText: item?.substitutionText ?? '',
        };
        if (item.mapItemUid) {
          serviceSiteMappingItem.MapItemUid = item.mapItemUid;
        }

        ServiceSiteMapping.push(serviceSiteMappingItem);
      }
    });

    const modifyItemDto: FcModifyInsurancePayerListItemDto = {
      // Contact Info
      Corporation: {
        Address: {
          CountyName: contactInfo.address.countyName,
          CountyCode: contactInfo.address.countyCode,
          Address1: contactInfo.address.addressOne,
          Address2: contactInfo.address.addressTwo,
          City: contactInfo.address.city,
          State: contactInfo.address.state,
          Zip: contactInfo.address.zip,
          Country: contactInfo.address.country,
        },
        PrimaryContact: contactInfo.primaryContact,
        SecondaryContact: contactInfo.secondaryContact,
        Id: contactInfo.Id,
        Name: contactInfo.name,
        Inactive: contactInfo.inactive,
      },
      ExternalID: contactInfo.externalId,
      Name: contactInfo.name,
      Notes: contactInfo.notes,

      // Settings
      AccountTypeUid: this.parseFindControlValue(settings.AccountTypeUid),
      UseProviderFeeSchedule: settings.UseProviderFeeSchedule,
      FeeScheduleUid: this.parseFindControlValue(settings.FeeScheduleUid),
      AmountAllowedFeeScheduleUid: this.parseFindControlValue(settings.AmountAllowedFeeScheduleUid),
      ReimbursementRuleUid: this.parseFindControlValue(settings.ReimbursementRuleUid),
      ERAPayerID: settings.ERAPayerID,
      PrmRuleGroupUid: this.parseFindControlValue(settings.PrmRuleGroupUid),
      AutoWriteoffCreditTypeUid: this.parseFindControlValue(settings.AutoWriteoffCreditTypeUid),
      NoNetwork: settings.NoNetwork,
      EligibilityClearingHouseUid: this.parseFindControlValue(settings.EligibilityClearingHouseUid),
      EligibilityPayerUid: this.parseFindControlValue(settings.EligibilityPayerUid),
      Eligibility_ListClaimFormatUid: this.parseFindControlValue(settings.EligibilityFormatUid),
      EnrollmentClearingHouseUid: this.parseFindControlValue(settings.EnrollmentClearingHouseUid),
      DemographicsClearingHouseUid: this.parseFindControlValue(settings.DemographicsClearingHouseUid),
      AlternateInsurancePlanUid: this.parseFindControlValue(settings.AlternateInsurancePlanUid),
      ICD10Date: settings.ICD10Date.toISOString(),
      EM2021Date: settings.EM2021Date.toISOString(),
      MapToPayerTypeCode: settings.MapToPayerTypeCode,
      ReferralRequired: settings.ReferralRequired,
      EligibleCCM_CPO: settings.EligibleCCM_CPO,
      RequiresABNChecking: settings.RequiresABNChecking,
      EnglishFormattingTemplateUid: this.parseFindControlValue(settings.EnglishFormattingTemplateUid),
      SpanishFormattingTemplateUid: this.parseFindControlValue(settings.SpanishFormattingTemplateUid),

      // Claims
      ClearinghouseUid: this.parseFindControlValue(claims.ClearinghouseUid),
      ElectronicPayerUid: this.parseFindControlValue(claims.ElectronicPayerUid),
      ElectronicClaimFormatUid: this.parseFindControlValue(claims.ElectronicClaimFormatUid),
      PaperClaimFormatUid: this.parseFindControlValue(claims.PaperClaimFormatUid),
      PaperClaimDefaultOptionUid: this.parseFindControlValue(claims.PaperClaimDefaultOptionUid),
      ScrubProfileUid: this.parseFindControlValue(claims.ScrubProfileUid),
      AcceptAssignment,
      AcceptAssignmentLabOnly,
      MemberIDFormat: claims.MemberIDFormat,
      GroupIDFormat: claims.GroupIDFormat,
      ClaimStatusPayerUid: this.parseFindControlValue(claims.ClaimStatusPayerUid),

      // Labs / Providers
      PreferredLaboratoriesUid: this.parseFindControlValue(labsProviders.PreferredLaboratoriesUid, true),
      RelInsuranceCarrierProviderExclusion: this.parseFindControlValue(labsProviders.RelInsuranceCarrierProviderExclusion, true),

      // Procedure Mapping
      ProcedureCodeMapping,

      // Payer ID Override
      ServiceSiteMapping,
    };

    if (
      contactInfo.phoneOne.phoneNumber &&
      contactInfo.phoneOne.phoneType &&
      contactInfo.phoneOne.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone1 = {
        PhoneNumber: contactInfo.phoneOne.phoneNumber,
        PhoneType: contactInfo.phoneOne.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneOne.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneTwo.phoneNumber &&
      contactInfo.phoneTwo.phoneType &&
      contactInfo.phoneTwo.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone2 = {
        PhoneNumber: contactInfo.phoneTwo.phoneNumber,
        PhoneType: contactInfo.phoneTwo.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneTwo.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneThree.phoneNumber &&
      contactInfo.phoneThree.phoneType &&
      contactInfo.phoneThree.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone3 = {
        PhoneNumber: contactInfo.phoneThree.phoneNumber,
        PhoneType: contactInfo.phoneThree.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneThree.phoneTypeUid),
      };
    }
    if (
      contactInfo.phoneFour.phoneNumber &&
      contactInfo.phoneFour.phoneType &&
      contactInfo.phoneFour.phoneTypeUid &&
      modifyItemDto.Corporation != null
    ) {
      modifyItemDto.Corporation.Phone4 = {
        PhoneNumber: contactInfo.phoneFour.phoneNumber,
        PhoneType: contactInfo.phoneFour.phoneType,
        PhoneTypeUid: this.parseFindControlValue(contactInfo.phoneFour.phoneTypeUid),
      };
    }
    super.save<FcModifyInsurancePayerListItemDto>(modifyItemDto);
  }
}
